/* © CNES */
import * as ol from 'ol';
import TileLayer from 'ol/layer/WebGLTile';
import { Projection, transformExtent } from 'ol/proj';
import { register } from 'ol/proj/proj4.js';
import { GeoTIFF } from 'ol/source';
import proj4 from 'proj4';
import { useEffect, useRef } from 'react';
import { osm } from '../../map/sources';
import './Geotiff.scss';

/**
 *  Geotiff displays a map with a geotiff image for quicklooks.
 * @param blob the tiff image blob to show.
 * @param processName the processName to show tiff from.
 * @returns Element.
 */
export const Geotiff = ({
  blob,
  processName,
}: {
  blob: Blob | undefined;
  processName: string | undefined;
}) => {
  const mapRefGeotiff = useRef<any>();
  const source =
    processName === 'ndvi-s2st'
      ? new GeoTIFF({
          normalize: true,
          sources: [
            {
              blob,
              min: -0.9,
              max: 1,
            },
          ],
        })
      : new GeoTIFF({
          sources: [
            {
              blob,
            },
          ],
        });

  // Create map on component mount.
  useEffect(() => {
    let mapObject = new ol.Map({
      controls: [],
      target: 'map',
      layers: [
        new TileLayer({
          source: osm(),
          zIndex: 0,
        }),
        new TileLayer({
          style:
            processName === 'ndvi-s2st'
              ? {
                  color: [
                    'interpolate',
                    ['linear'],
                    // calculate NDVI, bands come from the sources below
                    [
                      '/',
                      ['-', ['band', 2], ['band', 1]],
                      ['+', ['band', 2], ['band', 1]],
                    ],
                    -1,
                    [191, 191, 191, 0],
                    -0.2,
                    [191, 191, 191],
                    -0.1,
                    [219, 219, 219],
                    0,
                    [255, 255, 224],
                    0.025,
                    [255, 250, 204],
                    0.05,
                    [237, 232, 181],
                    0.075,
                    [222, 217, 156],
                    0.1,
                    [204, 199, 130],
                    0.125,
                    [189, 184, 107],
                    0.15,
                    [176, 194, 97],
                    0.175,
                    [163, 204, 89],
                    0.2,
                    [145, 191, 82],
                    0.25,
                    [128, 179, 71],
                    0.3,
                    [112, 163, 64],
                    0.35,
                    [97, 150, 54],
                    0.4,
                    [79, 138, 46],
                    0.45,
                    [64, 125, 36],
                    0.5,
                    [48, 110, 28],
                    0.55,
                    [33, 97, 18],
                    0.6,
                    [15, 84, 10],
                    0.65,
                    [0, 69, 0],
                    1,
                    [0, 69, 0],
                  ],
                }
              : {},
          source: source,
        }),
      ],
      view: new ol.View(),
    });

    // register quicklook projection to reproject view on map.
    source.getView().then((options) => {
      const projectionCode = (options.projection as Projection).getCode();
      proj4.defs(
        `${projectionCode}`,
        `+proj=utm +zone=${projectionCode.slice(
          -2
        )} +datum=WGS84 +units=m +no_defs +type=crs`
      );
      register((proj4 as any).default || proj4);

      mapObject
        .getView()
        .fit(
          transformExtent(
            options?.extent ?? [],
            options.projection,
            mapObject.getView().getProjection()
          )
        );
    });

    mapObject.setTarget(mapRefGeotiff.current);

    return () => mapObject.setTarget();
    // eslint-disable-next-line
  }, [blob]);

  return <div ref={mapRefGeotiff} className="ol-map-geotiff"></div>;
};
